import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import { REACT_APP_BACKEND_PROTECTED_URL } from "../../configs/config";

const TERMS_LAST_UPDATED = "2026-03-21";
const PRIVACY_LAST_UPDATED = "2026-03-23";

const COOKIE_TABLE_ROWS = [
  { name: "cc_cookie", purpose: "Stores cookie consent preferences", duration: "6 months", category: "Necessary" },
  {
    name: "_ga",
    purpose: "Google Analytics - distinguishes unique visitors",
    duration: "2 years",
    category: "Analytics",
  },
  {
    name: "_ga_K8RRP1QWH4",
    purpose: "Google Analytics - maintains session state",
    duration: "2 years",
    category: "Analytics",
  },
  {
    name: "personalization_id",
    purpose: "X (Twitter) Ads - tracks ad campaign performance and conversions",
    duration: "2 years",
    category: "Marketing",
  },
];

const Agreement: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();

  const handleAgree = async () => {
    if (!user?.sub) {
      console.error("No user ID available");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      const agreementData = {
        agreed: true,
        timestamp: new Date().toISOString(),
        version: "1.0",
        userId: user.sub,
        userEmail: user.email,
        userAgent: navigator.userAgent,
        consentType: "Twilio communications opt-in",
        consentDetails: {
          communicationsConsent: true,
          termsAccepted: true,
          privacyPolicyAccepted: true,
        },
      };

      // Store in backend
      const response = await fetch(`${REACT_APP_BACKEND_PROTECTED_URL}/optIn/${user.sub}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agreementData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Store in localStorage for immediate access
      console.log("Agreement data:", response.body);
      localStorage.setItem("userAgreement", JSON.stringify(agreementData));
      navigate("/");
    } catch (error) {
      console.error("Failed to save agreement:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Terms & Privacy — Muggle Test</title>
        <meta
          name="description"
          content="Muggle Test terms and privacy policy, plus communications consent for product usage."
        />
        <link rel="canonical" href="https://www.muggle-ai.com/muggletestv0/terms" />
      </Helmet>

      <Paper elevation={3} sx={{ borderRadius: 2, backgroundColor: "hsl(40, 33%, 98%)", color: "hsl(265, 20%, 15%)" }}>
      <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Typography variant="h4" gutterBottom>
          Terms & Privacy
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Terms last updated: {TERMS_LAST_UPDATED}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Privacy last updated: {PRIVACY_LAST_UPDATED}
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
          Terms of Service
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          1. Agreement to terms
        </Typography>
        <Typography paragraph>
          These Terms of Service (“Terms”) govern your access to and use of Muggle AI’s websites, applications, and
          related services (collectively, the “Services”). By accessing or using the Services, you agree to be bound by
          these Terms. If you do not agree, do not use the Services.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          2. The Services
        </Typography>
        <Typography paragraph>
          We provide software and related tools to help you plan, run, and manage automated testing and related workflows.
          Features, availability, and limits may change over time. We may suspend or discontinue parts of the Services
          with reasonable notice where practicable.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          3. Accounts and security
        </Typography>
        <Typography paragraph>
          You are responsible for maintaining the confidentiality of your account credentials and for all activity under
          your account. You must provide accurate registration information and notify us promptly of any unauthorized use.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          4. Acceptable use
        </Typography>
        <Typography paragraph>
          You will not misuse the Services, including by probing, scanning, or testing systems you are not authorized to
          test; attempting to gain unauthorized access; interfering with the Services; or using the Services in violation
          of law or third-party rights. We may investigate and take action, including termination, for violations.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          5. Intellectual property
        </Typography>
        <Typography paragraph>
          The Services, including software, branding, and documentation, are owned by Muggle AI or its licensors and are
          protected by intellectual property laws. Except for the limited right to use the Services as permitted by these
          Terms, no rights are granted to you.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          6. Third-party services
        </Typography>
        <Typography paragraph>
          The Services may integrate with or link to third-party services. Those services are governed by their own terms
          and privacy policies. We are not responsible for third-party services.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          7. Disclaimers
        </Typography>
        <Typography paragraph>
          THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR
          IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT,
          TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          8. Limitation of liability
        </Typography>
        <Typography paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, MUGGLE AI AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AND SUPPLIERS WILL
          NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
          DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY
          OF SUCH DAMAGES. OUR AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICES WILL NOT EXCEED THE GREATER OF
          THE AMOUNTS YOU PAID US FOR THE SERVICES IN THE TWELVE MONTHS BEFORE THE CLAIM OR ONE HUNDRED U.S. DOLLARS
          (US$100), EXCEPT WHERE PROHIBITED BY LAW.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          9. Changes
        </Typography>
        <Typography paragraph>
          We may modify these Terms from time to time. We will post the updated Terms on this page and update the “Last
          updated” date. Material changes may require additional notice where required by law. Continued use after changes
          become effective constitutes acceptance of the revised Terms.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Privacy Policy
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          1. Introduction
        </Typography>
        <Typography paragraph>
          This Privacy Policy explains how Muggle AI Ltd (“Muggle Test”, “we”, “us”) collects, uses, and protects your
          information when you use our website and services at muggle-ai.com.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          2. Data we collect
        </Typography>
        <Typography component="div" paragraph>
          <ul>
            <li>
              <strong>Account data</strong> - email address and name provided via Auth0 authentication.
            </li>
            <li>
              <strong>Usage data</strong> - pages visited, features used, and interaction patterns.
            </li>
            <li>
              <strong>Device data</strong> - browser type, operating system, and screen resolution collected via Google
              Analytics 4.
            </li>
            <li>
              <strong>Payment data</strong> - processed by Stripe. We do not store credit card numbers.
            </li>
          </ul>
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          3. How we use data
        </Typography>
        <Typography component="div" paragraph>
          <ul>
            <li>Provide, maintain, and improve the Services.</li>
            <li>Process payments and manage subscriptions.</li>
            <li>Send service-related communications (e.g. billing receipts, security alerts).</li>
            <li>Analyze usage patterns in anonymized, aggregate form.</li>
            <li>Detect, prevent, and respond to fraud or abuse.</li>
          </ul>
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          4. Cookies & consent
        </Typography>
        <Typography paragraph>
          We use a small number of cookies to operate and analyze the Services.
        </Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cookie</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {COOKIE_TABLE_ROWS.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>
                    <code>{row.name}</code>
                  </TableCell>
                  <TableCell>{row.purpose}</TableCell>
                  <TableCell>{row.duration}</TableCell>
                  <TableCell>{row.category}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography paragraph>
          You can manage your cookie preferences at any time using the Cookie Settings link in the footer.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          5. Third-party services
        </Typography>
        <Typography paragraph>
          We rely on the following third-party providers to operate the Services: Google Analytics 4 (usage analytics),
          X (Twitter) Ads (marketing and conversion tracking), Auth0 (authentication), Stripe (payment processing), and
          Microsoft Azure (cloud infrastructure). Each provider operates under its own privacy policy.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          6. Data retention
        </Typography>
        <Typography component="div" paragraph>
          <ul>
            <li>Account data is retained while your account is active.</li>
            <li>Google Analytics data is retained for 14 months.</li>
            <li>You may request deletion of your data at any time by contacting us.</li>
          </ul>
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          7. Your rights (GDPR)
        </Typography>
        <Typography paragraph>
          If you are located in the European Economic Area, you have the right to access, rectify, erase, restrict
          processing of, and request portability of your personal data. You also have the right to object to processing.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          8. Changes
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated “Last
          updated” date.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Communications Consent
        </Typography>
        <Typography paragraph>
          By checking the box below, you expressly consent to receive communications from us, including through the use
          of automated technology, artificial intelligence, or autodialed calls and text messages at the phone number(s)
          you provide. You understand that:
        </Typography>

        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li">Your consent is not required to purchase any goods or services</Typography>
          <Typography component="li">Message and data rates may apply</Typography>
          <Typography component="li">You may receive periodic recurring messages</Typography>
          <Typography component="li">
            You can opt-out at any time by texting STOP or through your account settings
          </Typography>
          <Typography component="li">Messaging frequency may vary</Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Contact
        </Typography>
        <Typography paragraph>
          Questions about Terms & Privacy:{" "}
          <MuiLink href="mailto:support@muggle-ai.com" color="primary">
            support@muggle-ai.com
          </MuiLink>
          .
        </Typography>

        <Box sx={{ mt: 3, mb: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
            label={
              <Typography variant="body2">
                I agree to the Terms & Privacy and consent to receive communications as described
                above, including automated calls and text messages. I understand that my consent is not required to make
                a purchase.
              </Typography>
            }
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" disabled={!agreed || isSubmitting} onClick={handleAgree} size="large">
            {isSubmitting ? "Saving..." : "I Agree & Continue"}
          </Button>
        </Box>
      </Box>
    </Paper>
    </>
  );
};

export default Agreement;
